import { useState, useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router";

import { User } from "../../types";

import logo from "../../assets/images/cureapp.png";
import menu from "../../assets/icons/menu.svg";
import { HamburgerMenu } from "./HamburgerMenu";
import { hexToRGB } from "../../utils/style";
import { Typography } from "../atoms/Typography";
import { useOnClickOutside } from "../../hooks/UseOnClickOutside";

/**
 * HEADER
 */

export type HeaderProps = {
  message?: string;
  user: Partial<User>;
  medicalInstitutionName?: string;
  version?: string;
  logoutClick: () => void;
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 50px;
  border-bottom: 2px solid ${(props) => hexToRGB(props.theme.colors.brand, 0.4)};
  background-color: ${(props) => props.theme.colors.primaryBackground};
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;

const LogoWrapper = styled.div`
  cursor: pointer;
`;

const MessageWrapper = styled.div`
  padding: 8px;
  margin-left: 20px;
  overflow: hidden;
  background-color: ${(props) =>
    hexToRGB(props.theme.colors.secondaryBackground, 0.08)};
  border-radius: 4px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
`;

const UserBlock = styled.div`
  overflow: hidden;
  padding-right: 18px;
  border-right: 1px solid
    ${(props) => hexToRGB(props.theme.colors.primaryBorder, 0.5)};
`;

const NameWrapper = styled.div`
  p {
    text-align: right;
  }
`;

const MedicalInstitutionNameWrapper = styled(NameWrapper)`
  && {
    margin-bottom: 6px;
    opacity: 0.7;
  }
`;

const IconArea = styled.div`
  position: relative;
`;

const IconWrapper = styled.div`
  margin-left: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export const Header = ({
  message,
  user,
  medicalInstitutionName,
  version,
  logoutClick,
}: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const menuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => setIsMenuOpen(false));

  return (
    <Container>
      <Left>
        <Link to={"/"}>
          <LogoWrapper>
            <img src={logo} width="22" height="22" alt={"logo"} />
          </LogoWrapper>
        </Link>
        {version && (
          <MessageWrapper>
            <Typography size={1}>version: {version}</Typography>
          </MessageWrapper>
        )}
        {message && (
          <MessageWrapper>
            <Typography size={1} withEllipsis={true}>
              {message}
            </Typography>
          </MessageWrapper>
        )}
      </Left>
      {user.name && (
        <Right>
          <UserBlock>
            <MedicalInstitutionNameWrapper>
              <Typography size={1}>{medicalInstitutionName}</Typography>
            </MedicalInstitutionNameWrapper>
            <NameWrapper>
              <Typography size={1} bold>
                {user.name}
              </Typography>
            </NameWrapper>
          </UserBlock>
          <IconArea ref={menuRef}>
            <IconWrapper onClick={() => toggleMenu()}>
              <img src={menu} alt={"menu"} />
            </IconWrapper>
            <HamburgerMenu
              user={user}
              isOpen={isMenuOpen}
              toggleMenu={toggleMenu}
              onLogoutClick={logoutClick}
            />
          </IconArea>
        </Right>
      )}
    </Container>
  );
};
